import { ClientListItem } from 'src/admin/users/types';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useClients } from 'src/clients/hooks/useClients';
import { Family, useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { familyRoleLabel } from 'src/clients/utils';

export type FamilyMember = {
  clientId: string;
  client: ClientListItem;
  /** Role in a role-model family, relationship to the main member in a star family, else null. */
  roleLabel: string | null;
};

// Single source of truth for resolving a family's members. Both the family
// screen and the individual screen build their member list from this so the two
// can't drift: role-model families keep every member in `family.members` with no
// secondary rows, so those must be unioned in or non-primary members get hidden
// when the family is read through the star shape.
export function buildFamilyMembers(
  family: Family | null,
  clientById: Record<string, ClientListItem>,
  roleNameById: Record<string, string>,
): FamilyMember[] {
  if (!family) return [];

  const mainClientId = family.mainFamilyMember.clientId;
  const secondaryIds = family.secondaryFamilyMembers.map(m => m.clientId);
  const mainClient = clientById[mainClientId];

  const seen = new Set([mainClientId, ...secondaryIds]);
  const roleModelIds = family.members.map(m => m.clientId).filter(id => !seen.has(id));

  const secondaryMembers: FamilyMember[] = [...secondaryIds, ...roleModelIds]
    .map(id => {
      const c = clientById[id];
      if (!c) return null;
      return {
        clientId: id,
        client: c,
        roleLabel: familyRoleLabel(family, roleNameById, id),
      };
    })
    .filter((m): m is FamilyMember => m !== null)
    .sort((a, b) => {
      const aName = `${a.client.firstName} ${a.client.lastName}`.toLowerCase();
      const bName = `${b.client.firstName} ${b.client.lastName}`.toLowerCase();
      return aName.localeCompare(bName);
    });

  return [
    ...(mainClient
      ? [
          {
            clientId: mainClientId,
            client: mainClient,
            roleLabel: familyRoleLabel(family, roleNameById, mainClientId),
          },
        ]
      : []),
    ...secondaryMembers,
  ];
}

export function useFamilyMembers({ enabled = true }: { enabled?: boolean } = {}): {
  members: FamilyMember[];
  hasOtherMembers: boolean;
  isLoading: boolean;
} {
  // Not useCurrentClient: this hook is also called from the family route, which has a familyId
  // but no clientId, and useCurrentClient throws there. With no current client there is no
  // family to resolve from it, so the guard below returns no members.
  const { client, clientId } = useCurrentClientIfAvailable();
  const familyId = client?.familyId;

  const { family, isLoading: isFamilyLoading } = useFamily(enabled ? familyId : undefined);
  const { clients, isLoading: isClientsLoading } = useClients(
    enabled && familyId ? { familyId } : {},
    { enabled },
  );
  const { roleNameById } = useFamilyRoles({ enabled });

  const isLoading = isFamilyLoading || isClientsLoading;

  if (!familyId || !family) {
    return { members: [], hasOtherMembers: false, isLoading };
  }

  const clientById = Object.fromEntries(clients.map(c => [c.id, c]));

  const members = buildFamilyMembers(family, clientById, roleNameById);

  const hasOtherMembers = members.some(m => m.clientId !== clientId);

  return { members, hasOtherMembers, isLoading };
}
